import { MoreVert } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import OutboxIcon from '@mui/icons-material/Outbox';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import {
  Box,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  SwipeableDrawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Contact } from '../../../../../../types/api';
import { useAuth } from '../../../hooks';
import { queryClient } from '../../../queries/queryClient';
import {
  resendInvitationToAccountContactAPI,
  resendVerificationToAccountContactAPI,
} from '../../../services/apis';
import noticeError from '../../../utils/errors/noticeError';
import { Tracker } from '../../../utils/tracker';
import ContactDeactivateModal from './ContactDeactivateModal';

type Props = {
  contact: Contact;
  setModalState: (state: { open: boolean; contact?: Contact }) => void;
};

export default function ContactRowActionMenu({
  contact,
  setModalState,
}: Props) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const tracker = Tracker.getInstance();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    setAnchorEl(null);
    setModalState({ open: true, contact });
  };

  const handleDeactivate = () => {
    setAnchorEl(null);
    setDeactivateOpen(true);
  };

  const handleResendInvitation = async () => {
    setAnchorEl(null);
    setLoading(true);
    try {
      await resendInvitationToAccountContactAPI(contact.id);
      queryClient.invalidateQueries('contacts');
      queryClient.invalidateQueries('activity');
      queryClient.invalidateQueries('application-access-contacts');
      toast.success('Invitation resent successfully!');
      tracker.track(Tracker.EVENTS.USER_RESENT_INVITATION, {
        user_id: contact.id,
      });
    } catch (error) {
      console.error(error);
      noticeError(error);
      toast.error(error?.message ?? 'Something went wrong!');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setAnchorEl(null);
    setLoading(true);
    try {
      await resendVerificationToAccountContactAPI(contact.id);
      queryClient.invalidateQueries('contacts');
      queryClient.invalidateQueries('activity');
      queryClient.invalidateQueries('application-access-contacts');
      toast.success('Verification resent successfully!');
      tracker.track(Tracker.EVENTS.USER_RESENT_VERIFICATION, {
        user_id: contact.id,
      });
    } catch (error) {
      console.error(error);
      noticeError(error);
      toast.error(error?.message ?? 'Something went wrong!');
    } finally {
      setLoading(false);
    }
  };

  if (!contact?.id) return null;

  const actionItems = (
    <>
      <MenuItem onClick={handleEdit}>
        <ListItemIcon sx={{ mr: 0 }}>
          <EditIcon sx={{ color: 'text.secondary' }} />
        </ListItemIcon>
        <ListItemText primary="Edit" />
      </MenuItem>

      {contact.status === null ? (
        <MenuItem onClick={handleResendInvitation}>
          <ListItemIcon sx={{ mr: 0 }}>
            <OutboxIcon sx={{ color: 'text.secondary' }} />
          </ListItemIcon>
          <ListItemText primary="Send Invitation" />
        </MenuItem>
      ) : null}

      {contact.status === 'Invited' ? (
        <MenuItem onClick={handleResendInvitation}>
          <ListItemIcon sx={{ mr: 0 }}>
            <OutboxIcon sx={{ color: 'text.secondary' }} />
          </ListItemIcon>
          <ListItemText primary="Resend Invitation" />
        </MenuItem>
      ) : null}

      {contact.status === 'Unverified' ? (
        <MenuItem onClick={handleResendVerification}>
          <ListItemIcon sx={{ mr: 0 }}>
            <HowToRegIcon sx={{ color: 'text.secondary' }} />
          </ListItemIcon>
          <ListItemText primary="Resend Verification Email" />
        </MenuItem>
      ) : null}

      {contact.status !== 'Inactive' && contact.email !== user?.email ? (
        <MenuItem onClick={handleDeactivate}>
          <ListItemIcon sx={{ mr: 0 }}>
            <PersonRemoveIcon sx={{ color: 'text.secondary' }} />
          </ListItemIcon>
          <ListItemText primary="Deactivate" />
        </MenuItem>
      ) : null}

      {contact.status === 'Inactive' ? (
        <MenuItem onClick={handleDeactivate}>
          <ListItemIcon sx={{ mr: 0 }}>
            <PersonAddIcon sx={{ color: 'text.secondary' }} />
          </ListItemIcon>
          <ListItemText primary="Activate" />
        </MenuItem>
      ) : null}
    </>
  );

  return (
    <>
      <IconButton
        onClick={handleClick}
        disabled={loading}
        sx={{ p: isMobile ? 0 : 1 }}
      >
        {loading ? <CircularProgress size={24} /> : <MoreVert />}
      </IconButton>

      {isMobile ? (
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onClose={handleClose}
          onOpen={() => {}}
          disableSwipeToOpen
          PaperProps={{
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              pb: 1,
            },
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 4,
              bgcolor: 'divider',
              borderRadius: 2,
              mx: 'auto',
              my: 1,
            }}
          />
          {actionItems}
        </SwipeableDrawer>
      ) : (
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          {actionItems}
        </Menu>
      )}

      <ContactDeactivateModal
        contact={contact}
        open={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
      />
    </>
  );
}
